import { useState } from 'react';
import { CalendarClock, Play } from 'lucide-react';
import cronstrue from 'cronstrue';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CronEditDialog } from '../components/CronEditDialog';
import {
  useMastraJobsList,
  usePatchMastraJob,
  useRunMastraJob,
} from '../hooks/useScheduledJobs';
import type { MastraJob } from '../types';

const MTL_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Montreal',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatNextRun(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  try {
    return MTL_FORMAT.format(d);
  } catch {
    return '—';
  }
}

function humanizeCron(expr: string): string {
  try {
    return cronstrue.toString(expr);
  } catch {
    return expr;
  }
}

interface EditingState {
  job: MastraJob;
  error: string | null;
}

export function ScheduledJobsPage() {
  const { data: jobs, isLoading, error: listError, refetch } = useMastraJobsList();
  const patch = usePatchMastraJob();
  const run = useRunMastraJob();

  const [editing, setEditing] = useState<EditingState | null>(null);

  const openEdit = (job: MastraJob) => {
    setEditing({ job, error: null });
  };

  const closeEdit = () => {
    setEditing(null);
  };

  const handleSaveCron = (cron: string) => {
    if (!editing) return;
    patch.mutate(
      { id: editing.job.workflow_id, body: { cron } },
      {
        onSuccess: () => {
          setEditing(null);
        },
        onError: (err: Error & { status?: number }) => {
          if (err.status === 400) {
            setEditing((prev) => prev ? { ...prev, error: err.message } : null);
          } else {
            setEditing(null);
          }
        },
      },
    );
  };

  const handleToggleEnabled = (job: MastraJob) => {
    patch.mutate({ id: job.workflow_id, body: { enabled: !job.enabled } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 id="workflow-scheduler-heading" className="text-2xl font-bold flex items-center gap-2">
          <CalendarClock className="h-6 w-6 text-primary" />
          Workflow Scheduler
        </h1>
        <p className="text-muted-foreground">
          Manage recurring Mastra workflow triggers.
        </p>
      </div>

      {listError && (
        <div className="flex items-center gap-3">
          <p className="text-destructive text-sm">
            Failed to load scheduler jobs: {listError instanceof Error ? listError.message : 'Unknown error'}
          </p>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <Table aria-labelledby="workflow-scheduler-heading">
          <TableHeader>
            <TableRow>
              <TableHead>Workflow</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Timezone</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Next Run</TableHead>
              <TableHead className="w-36" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : !jobs || jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                  No scheduler jobs
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => {
                const isRunPending = run.isPending && run.variables === job.workflow_id;
                const isPatchPending = patch.isPending && patch.variables?.id === job.workflow_id;
                return (
                  <TableRow key={job.workflow_id}>
                    <TableCell>
                      <div className="font-bold text-sm">{job.workflow_id}</div>
                      {job.description && (
                        <div className="text-xs text-muted-foreground">{job.description}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{humanizeCron(job.cron)}</div>
                      <code className="text-xs text-muted-foreground">{job.cron}</code>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{job.tz ?? '—'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={job.enabled}
                        onCheckedChange={() => handleToggleEnabled(job)}
                        disabled={isPatchPending}
                        aria-label={`Toggle ${job.workflow_id}`}
                      />
                    </TableCell>
                    <TableCell className="text-sm">{formatNextRun(job.nextRun)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(job)}
                        >
                          Edit Schedule
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isRunPending}
                          onClick={() => run.mutate(job.workflow_id)}
                          aria-label={`Run ${job.workflow_id} now`}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <CronEditDialog
          open
          onOpenChange={(o) => { if (!o) closeEdit(); }}
          workflowId={editing.job.workflow_id}
          initialCron={editing.job.cron}
          timezone={editing.job.tz ?? 'UTC'}
          onSave={handleSaveCron}
          isSaving={patch.isPending && patch.variables?.id === editing.job.workflow_id}
          cronError={editing.error}
        />
      )}
    </div>
  );
}
